import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { Base64UrlService } from '@app/services/base64url.service';

@Injectable({
    providedIn: 'root',
})
export class ChecklistDataService {
    constructor(
        private datatask: DataTaskService,
        private base64: Base64UrlService,
    ) {}

    /**
     * Retrieves the checklist data settings for the checklist data page.
     *
     * @datatask 922
     * @datataskName sp_ws_p2_w_checklistdata_settings
     * @param params the keyno for the checklist to retrieve data for.
     * @returns promise containing settings for the checklist.
     */
    public async getChecklistDataSettings(params: ChecklistDataParams): Promise<ChecklistDataSetting> {
        const response = (await this.datatask.Post(922, params))[0];

        try {
            response.report_html = this.base64.urlDecode(response.report_html_base64);
        } catch (error) {
            console.error(error);
            response.report_html = '';
        }

        return response;
    }

    /**
     * Retrieves the checklist data for the given keyno.
     *
     * @datatask 231
     * @datataskName sp_ws_p2_checklistdata_get
     * @param params the keyno for the checklist data to get.
     * @returns promise containing the checklist data.
     */
    public async getChecklistData(params: ChecklistDataParams): Promise<ChecklistDataGet> {
        return (await this.datatask.Post(231, params))[0];
    }

    /**
     * Saves the given config of checklist data.
     *
     * @datatask 267
     * @datataskName sp_ws_p2_checklistdata_save
     * @param params
     * @returns promise containing errorocde and message.
     */
    public async saveChecklistData(params: SaveChecklistDataParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(267, params))[0];
    }

    /**
     * Saves the given config of checklist data.
     *
     * @datatask 262
     * @datataskName sp_ws_p2_select_checklist_delete
     * @param params
     * @returns promise containing errorocde and message.
     */
    public async deleteChecklistData(params: ChecklistDataParams): Promise<unknown> {
        return await this.datatask.Post(262, params);
    }

    /**
     * Computes values and validation for the given set of item and values.
     *
     * @datatask 1583
     * @datataskName sp_ws_p2_w_checklistdata_computevalidation
     * @param params the item keyno, value of list of values.
     * @returns promise containing validation and computed values to set back to the checklist.
     */
    public async computeValidation(params: ComputeValidationParams): Promise<ComputeValidationResponse> {
        return this.datatask.Post(1583, params);
    }
}

export interface SaveChecklistDataParams {
    checklistdata_keyno: string;
    checklist_keyno: string;
    checklist_name: string;
    approve: '0' | '1';
    checklistitems: { i: string; v: string }[];
}

export interface ComputeValidationParams {
    item_id: string;
    item_value: string;
    cvd: ComputeValidationData[];
}

export interface ComputeValidationData {
    /**
     * Checklist item id.
     */
    cii: string;

    /**
     * Checklist item value.
     */
    civ: string;
}

export interface ComputeValidationResponse {
    errorcode: '1' | '0';
    errormessage: string;
    outputresult?: ComputeValidationDataResult[];
}

export interface ComputeValidationDataResult {
    /**
     * Checklist item keyno
     */
    clik: string;

    /**
     * Checklist item value
     */
    cliv: string;
}

export interface ChecklistDataParams {
    checklistdata_keyno: string;
}

export interface ChecklistDataSetting {
    lock_to_portal_user_when_approved: '1' | '0';
    page_heading: string;
    report_html_base64: string;
    report_html: string;
    show_button_save: '1' | '0';
    show_button_save_and_send: '1' | '0';
    show_button_delete: '1' | '0';
    signature_dataformat: string;
}

export interface ChecklistDataGet {
    checklistdata_keyno: string;
    checklist_keyno: string;
    checklist_name: string;
    checkliststatus: string;
    nbr_of_pictures: string;
    paged: '1' | '0';
    checklistitems: ChecklistItem[];
}

export interface ChecklistItem {
    page_number: number;

    /**
     * The label/heading for the item.
     */
    checklistitem_method: string;

    /**
     * The element type of the item.
     */
    elementtype: ElementTypes;

    /**
     * Input variant.
     */
    type: string;

    /**
     * Typevar used in im-input-directive
     */
    typevar: string;

    /**
     * Id of the item.
     */
    checklistitem_keyno: string;

    /**
     * The model value of the item.
     */
    itemvalue: string;

    expected_result: string;

    /**
     * The untranslated value for `checklistitem_method` field.
     */
    word_id: string;

    /**
     * Whether the item should be visible or not.
     */
    visible: '1' | '0';

    /**
     * Whether the item is readonly or not.
     */
    r: '0' | '1';
    checklistitemdata_keyno: string;
    dep1_checklistitem_keyno: string;
    dep1_value: string;
    dep1_visible: '1' | '0';
    dep2_checklistitem_keyno: string;
    dep2_value: string;
    dep2_visible: '1' | '0';

    /**
     * Whether to compute validation.
     */
    cv: '0' | '1';

    /**
     * List of checklist items to compute validation against.
     */
    cvs: ComputeValidationItem[];

    /**
     * The options withing this item.
     */
    checklistitemitem: ChoiceListItem[];
}

interface ComputeValidationItem {
    /**
     * The checklist-item-keyno to compute validation against.
     */
    clik: string;
}

interface ChoiceListItem {
    /**
     * The label of the option.
     */
    checklistitemitem_name: string;

    /**
     * The id of the option.
     */
    checklistitemitem_keyno: string;

    /**
     * the orderby for the option in the list.
     */
    checklistitemitem_order: string;
}

type ElementTypes = ElementTypeHeading1 | ElementTypeHeading2 | ElementTypeChoiceList | ElementTypeCheckBox | ElementTypeTextInput | ElementTypeTextLong | ElementTypeDate | ElementTypeDateTime | ElementTypeTime | ElementTypeHTMLEditor | ElementTypeNumberInput | ElementTypeSignature;

type ElementTypeHeading1 = 'H1';
type ElementTypeHeading2 = 'H2';
type ElementTypeChoiceList = 'CL';
type ElementTypeTextLong = 'TL';
type ElementTypeTextInput = 'T';
type ElementTypeCheckBox = 'CB' | 'CS';
type ElementTypeDateTime = 'DT';
type ElementTypeDate = 'DA';
type ElementTypeTime = 'TI';
type ElementTypeHTMLEditor = 'HE';
type ElementTypeNumberInput = `N${number}` | 'N';
type ElementTypeSignature = 'SI';
